
import { mapState } from 'vuex';
import SingleReviewBody from './components/SingleReviewBody';
import MultipleReviewBody from './components/MultipleReviewBody';
import ReviewSource from '@/enums/ReviewSource';
import isMobileDevice from '@/lib/isMobileDevice';

const DEFAULT_HEIGHT = 294;

const PREVIEW_SAMPLE_PRODUCTS = [
  {
    id: 1,
    name: '[Sample] Orange Bucket Hat',
    extra_small_image_url:
      'https://assets.cre.ma/m/managing_reviews_preview_sub_order1.png'
  },
  {
    id: 2,
    name: '[Sample] Purple Shirt',
    extra_small_image_url:
      'https://assets.cre.ma/m/managing_reviews_preview_sub_order2.png'
  }
];

const PREVIEW_REMAINING_DAYS = 30;
const PREVIEW_TOTAL_REWARD = 2000;

export default {
  i18nComponentKey: 'ReviewPrompt.ReviewPrompt',
  name: 'ReviewPrompt',
  components: {
    SingleReviewBody,
    MultipleReviewBody
  },
  data() {
    return {
      isLoading: true,
      subOrders: [],
      maxMileageAmountPerOrderCents: Number.MAX_SAFE_INTEGER,
      maxMileageSubOrdersCountPerOrder: Number.MAX_SAFE_INTEGER,
      autoMileageSameProductInOrder: true
    };
  },
  computed: {
    ...mapState(['api']),
    ...mapState('window', ['windowService']),
    ...mapState('reviewSettings', ['reviewSettings', 'reviewPromptSettings']),
    totalMaxReward() {
      const limit = this.maxMileageAmountPerOrderCents;
      const countLimit = this.maxMileageSubOrdersCountPerOrder;

      const subOrdersByOrderId = {};
      this.subOrders.forEach(subOrder => {
        (subOrdersByOrderId[subOrder.order_id] =
          subOrdersByOrderId[subOrder.order_id] || []).push(subOrder);
      });

      return Object.values(subOrdersByOrderId).reduce(
        (total, orderSubOrders) => {
          const writableSubOrders = this.autoMileageSameProductInOrder
            ? orderSubOrders
            : orderSubOrders.filter(
                (subOrder, index) =>
                  orderSubOrders.findIndex(
                    other => other.product.id === subOrder.product.id
                  ) === index
              );
          const perSubOrder = writableSubOrders.map(subOrder => ({
            entries: subOrder.reward_items,
            mileage: subOrder.reward_items
              .filter(item => item.key !== 'first_review_per_order')
              .reduce((acc, item) => acc + item.value, 0)
          }));
          const firstReviewBonus = perSubOrder
            .flatMap(subOrder => subOrder.entries)
            .find(item => item.key === 'first_review_per_order');
          const bonus = firstReviewBonus ? firstReviewBonus.value : 0;
          const mileage = perSubOrder
            .sort((a, b) => b.mileage - a.mileage)
            .slice(0, countLimit)
            .reduce((acc, subOrder) => acc + subOrder.mileage, 0);
          return total + Math.min(mileage + bonus, limit);
        },
        0
      );
    },
    promptTitle() {
      return this.reviewPromptSettings.title
        .replace(/%{total_max_reward}/g, this.totalMaxReward.toLocaleString())
        .replace(
          /%{mileage_type_point_text}/g,
          this.reviewSettings.mileage_type_point_text
        );
    }
  },
  created() {
    this.previewMessageHandler = null;
  },
  mounted() {
    if (this.$route.query.preview_review_prompt) {
      this.requestPreview();
      return;
    }

    this.api
      .fetchReviewPrompt()
      .then(({ data }) => {
        const rewardInfo = data.reward_info || {};
        this.subOrders = data.sub_orders;
        this.maxMileageAmountPerOrderCents =
          rewardInfo.max_mileage_amount_per_order_cents ||
          Number.MAX_SAFE_INTEGER;
        this.maxMileageSubOrdersCountPerOrder =
          rewardInfo.max_mileage_sub_orders_count_per_order ||
          Number.MAX_SAFE_INTEGER;
        this.autoMileageSameProductInOrder =
          rewardInfo.auto_mileage_same_product_in_order !== false;
        if (this.subOrders.length === 0) {
          this.windowService.postMessage('set_close');
          return;
        }

        this.windowService.postMessage('init_popup', {
          width: 400,
          height: DEFAULT_HEIGHT,
          direction: this.reviewSettings.popup_direction,
          offset_x: this.reviewSettings.popup_offset_x,
          offset_y: this.reviewSettings.popup_offset_y,
          use_inducing_popup_dark_background: this.reviewSettings
            .use_inducing_popup_dark_background
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  beforeDestroy() {
    if (this.previewMessageHandler)
      this.windowService.removeMessageHandler(this.previewMessageHandler);
  },
  methods: {
    requestPreview() {
      this.previewMessageHandler = data => {
        if (data.act !== 'update:review_prompt') return;

        this.$store.commit(
          'reviewSettings/SET_REVIEW_PROMPT_SETTINGS',
          data.prompt_settings
        );
        this.subOrders = this.buildPreviewSubOrders(
          data.products || PREVIEW_SAMPLE_PRODUCTS
        );
        this.isLoading = false;
      };
      this.windowService.addMessageHandler(this.previewMessageHandler);

      this.windowService.postMessage('request:review_prompt');
    },
    buildPreviewSubOrders(products) {
      const expiresIn = new Date(Date.now() + PREVIEW_REMAINING_DAYS * 86400000)
        .toLocaleDateString('en-CA')
        .replace(/-/g, '.');

      return products.map((product, i) => ({
        id: i + 1,
        order_id: 1,
        product,
        product_options: {},
        reward_items:
          i === 0 ? [{ key: 'message', value: PREVIEW_TOTAL_REWARD }] : [],
        pending_review_expires_in: expiresIn,
        remaining_days: PREVIEW_REMAINING_DAYS
      }));
    },
    onClose() {
      this.windowService.postMessage('set_close');
    },
    onCloseToday() {
      this.windowService.postMessage('set_close_today');
    },
    onSubmit(params) {
      const review_source = isMobileDevice()
        ? ReviewSource.BRAND_MOBILE_POPUP
        : ReviewSource.BRAND_PC_POPUP;

      this.windowService.postMessage('open_new_review_popup', {
        ...params,
        sub_order_id: this.subOrders[0]?.id,
        review_source
      });
    }
  }
};
